import { ResponsiveTable } from '../components/ResponsiveTable';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DocumentTemplateDto,
  deleteDocumentTemplate,
  listDocumentTemplates,
  registerDocumentTemplate,
  replaceDocumentTemplateFile,
  updateDocumentTemplate,
  uploadDocumentTemplate,
} from '../services/documentTemplatesApi';
import { FileText, RefreshCw, Upload, Trash2, ToggleRight, ToggleLeft, Pencil, Download, FolderOpen, Eye, X } from 'lucide-react';
import { apiClient } from '../utils/api';

interface FormState {
  code: string;
  version: number;
  language: string;
  description: string;
  isActive: boolean;
  file: File | null;
}

export function DocumentTemplatesPage() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<DocumentTemplateDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<DocumentTemplateDto | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [editRequiredSigners, setEditRequiredSigners] = useState<string[]>(['CLIENT']);
  const [editSignaturePlacement, setEditSignaturePlacement] = useState<'LAST_PAGE' | 'INLINE'>('LAST_PAGE');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [replacingId, setReplacingId] = useState<string | null>(null);
  const replaceFileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>({
    code: '',
    version: 1,
    language: 'fr',
    description: '',
    isActive: true,
    file: null,
  });

  const sortedTemplates = useMemo(
    () =>
      [...templates].sort((a, b) =>
        a.code === b.code ? b.version - a.version : a.code.localeCompare(b.code),
      ),
    [templates],
  );

  const formatBytes = (bytes?: number | null) => {
    if (!bytes && bytes !== 0) return '—';
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, i);
    return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString();
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await listDocumentTemplates();
      setTemplates(data);
    } catch (err: any) {
      setError(err?.message || t('documentTemplates.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadTemplates();
  }, []);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'version' ? Number(value) : value,
    }));
  };

  const handleFileChange = (file: File | null) => {
    setForm((prev) => ({ ...prev, file }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.file) {
      setError(t('documentTemplates.errors.missingFile'));
      return;
    }
    if (!form.code || !form.language || !form.version) {
      setError(t('documentTemplates.errors.missingFields'));
      return;
    }

    try {
      setError(null);
      setSubmitting(true);

      const uploadResult = await uploadDocumentTemplate({
        file: form.file,
        code: form.code,
        language: form.language,
        version: form.version,
      });

      await registerDocumentTemplate({
        code: form.code,
        version: form.version,
        language: form.language,
        description: form.description || undefined,
        filePath: uploadResult.filePath,
        isActive: form.isActive,
      });

      await loadTemplates();

      setForm((prev) => ({
        ...prev,
        file: null,
        description: '',
      }));
    } catch (err: any) {
      setError(err?.message || t('documentTemplates.errors.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (template: DocumentTemplateDto) => {
    try {
      const updated = await updateDocumentTemplate(template.id, { isActive: !template.isActive });
      setTemplates((prev) =>
        prev.map((t) => (t.id === template.id ? { ...t, ...updated } : t)),
      );
    } catch (err: any) {
      setError(err?.message || t('documentTemplates.errors.updateFailed'));
    }
  };

  const ALL_SIGNER_ROLES = [
    { value: 'CLIENT', label: 'Client' },
    { value: 'LEGAL_GUARDIAN', label: 'Représentant légal' },
    { value: 'PRACTITIONER', label: 'Praticien' },
    { value: 'SHOP_MANAGER', label: 'Responsable du shop' },
  ];

  const handleOpenEdit = (template: DocumentTemplateDto) => {
    setEditingTemplate(template);
    setEditDescription(template.description ?? '');
    setEditIsActive(template.isActive);
    setEditRequiredSigners(
      template.requiredSigners && template.requiredSigners.length > 0
        ? template.requiredSigners
        : ['CLIENT'],
    );
    setEditSignaturePlacement(template.signaturePlacement ?? 'LAST_PAGE');
  };

  const toggleEditSigner = (role: string) => {
    setEditRequiredSigners((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  };

  const handleSaveEdit = async () => {
    if (!editingTemplate) return;
    try {
      setEditSubmitting(true);
      const updated = await updateDocumentTemplate(editingTemplate.id, {
        description: editDescription,
        isActive: editIsActive,
        requiredSigners: editRequiredSigners,
        signaturePlacement: editSignaturePlacement,
      });
      setTemplates((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
      setEditingTemplate(null);
    } catch (err: any) {
      setError(err?.message || t('documentTemplates.errors.updateFailed'));
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleSetDefaultPiercing = async (template: DocumentTemplateDto) => {
    const alreadyDefault = template.isDefaultPiercing;
    try {
      await updateDocumentTemplate(template.id, { isDefaultPiercing: !alreadyDefault });
      await loadTemplates();
    } catch (err: any) {
      setError(err?.message || 'Impossible de mettre à jour le modèle par défaut');
    }
  };

  const handleSetDefaultPiercingMinor = async (template: DocumentTemplateDto) => {
    const alreadyDefault = template.isDefaultPiercingMinor;
    try {
      await updateDocumentTemplate(template.id, { isDefaultPiercingMinor: !alreadyDefault });
      await loadTemplates();
    } catch (err: any) {
      setError(err?.message || 'Impossible de mettre à jour le modèle par défaut mineur');
    }
  };

  const handleSetDefaultTattoo = async (template: DocumentTemplateDto) => {
    const alreadyDefault = template.isDefaultTattoo;
    try {
      await updateDocumentTemplate(template.id, { isDefaultTattoo: !alreadyDefault });
      await loadTemplates();
    } catch (err: any) {
      setError(err?.message || 'Impossible de mettre à jour le modèle tatouage par défaut');
    }
  };

  const handleDownload = async (template: DocumentTemplateDto) => {
    try {
      const res = await apiClient(`/api/document-templates/${template.id}/file`);
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = template.filePath.split('/').pop() ?? `${template.code}_v${template.version}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err?.message || 'Impossible de télécharger le fichier');
    }
  };

  const handlePreview = async (template: DocumentTemplateDto) => {
    try {
      setPreviewLoadingId(template.id);
      const res = await apiClient(`/api/document-templates/${template.id}/pdf`);
      if (!res.ok) throw new Error('Preview failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (err: any) {
      setError(err?.message || 'Impossible de prévisualiser le fichier');
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const handleClosePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const handleReplaceFile = (template: DocumentTemplateDto) => {
    setReplacingId(template.id);
    setTimeout(() => replaceFileInputRef.current?.click(), 0);
  };

  const handleReplaceFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !replacingId) return;
    e.target.value = '';
    try {
      setError(null);
      const updated = await replaceDocumentTemplateFile(replacingId, file);
      setTemplates((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
    } catch (err: any) {
      setError(err?.message || 'Impossible de remplacer le fichier');
    } finally {
      setReplacingId(null);
    }
  };

  const handleDelete = async (template: DocumentTemplateDto) => {
    if (!window.confirm(t('documentTemplates.confirm.delete'))) {
      return;
    }
    try {
      await deleteDocumentTemplate(template.id);
      setTemplates((prev) => prev.filter((t) => t.id !== template.id));
    } catch (err: any) {
      setError(err?.message || t('documentTemplates.errors.deleteFailed'));
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="dt-list-toolbar flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <FileText className="h-6 w-6 text-primary-600" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{t('documentTemplates.title')}</h1>
            <p className="text-sm text-gray-600">{t('documentTemplates.subtitle')}</p>
          </div>
        </div>
        <button
          onClick={() => void loadTemplates()}
          className="inline-flex items-center px-3 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {t('actions.refresh')}
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700 border border-red-100">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-4 border">
        <h2 className="text-lg font-medium text-gray-900">{t('documentTemplates.form.title')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('documentTemplates.form.code')} *
            </label>
            <input
              name="code"
              value={form.code}
              onChange={handleInput}
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="TATTOO_ADULT"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('documentTemplates.form.language')} *
            </label>
            <input
              name="language"
              value={form.language}
              onChange={handleInput}
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="fr"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('documentTemplates.form.version')} *
            </label>
            <input
              name="version"
              type="number"
              min={1}
              value={form.version}
              onChange={handleInput}
              className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              required
            />
          </div>
          <div className="flex items-center space-x-3 mt-6">
            <label className="text-sm font-medium text-gray-700">
              {t('documentTemplates.form.active')}
            </label>
            <button
              type="button"
              onClick={() => setForm((prev) => ({ ...prev, isActive: !prev.isActive }))}
              className="flex items-center text-sm text-gray-700"
            >
              {form.isActive ? (
                <ToggleRight className="h-6 w-6 text-green-600" />
              ) : (
                <ToggleLeft className="h-6 w-6 text-gray-400" />
              )}
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            {t('documentTemplates.form.description')}
          </label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleInput}
            className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            rows={3}
            placeholder={t('documentTemplates.form.descriptionPlaceholder')}
          />
        </div>

        <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700">{t('documentTemplates.form.file')}</p>
            <p className="text-xs text-gray-500">{t('documentTemplates.form.fileHint')}</p>
          </div>
          <div className="mt-3 md:mt-0">
            <input
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
            />
            {form.file && <p className="text-xs text-gray-600 mt-1">{form.file.name}</p>}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            <Upload className="h-4 w-4 mr-2" />
            {submitting ? t('documentTemplates.actions.saving') : t('documentTemplates.actions.uploadAndSave')}
          </button>
        </div>
      </form>

      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">{t('documentTemplates.list.title')}</h2>
          <span className="text-sm text-gray-600">
            {t('documentTemplates.list.count', { count: sortedTemplates.length })}
          </span>
        </div>
        <div className="overflow-x-auto">
          <ResponsiveTable className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('documentTemplates.table.code')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('documentTemplates.table.language')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('documentTemplates.table.version')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('documentTemplates.table.active')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Piercing adulte
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Piercing mineur
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Tatouage
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('documentTemplates.table.file')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('documentTemplates.table.size')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('documentTemplates.table.modified')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('documentTemplates.table.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && (
                <tr>
                  <td className="px-4 py-4 text-sm text-gray-500" colSpan={11}>
                    {t('states.loading')}
                  </td>
                </tr>
              )}
              {!loading && sortedTemplates.length === 0 && (
                <tr>
                  <td className="px-4 py-4 text-sm text-gray-500" colSpan={11}>
                    {t('documentTemplates.list.empty')}
                  </td>
                </tr>
              )}
              {!loading &&
                sortedTemplates.map((template) => (
                  <tr key={template.id}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{template.code}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{template.language}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{template.version}</td>
                    <td className="px-4 py-3 text-sm">
                      <button
                        onClick={() => void handleToggleActive(template)}
                        className="inline-flex items-center text-sm text-gray-700"
                        title={t('documentTemplates.table.toggle')}
                      >
                        {template.isActive ? (
                          <ToggleRight className="h-5 w-5 text-green-600" />
                        ) : (
                          <ToggleLeft className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <button
                        onClick={() => void handleSetDefaultPiercing(template)}
                        className="inline-flex items-center gap-1.5 text-sm"
                        title={template.isDefaultPiercing ? 'Retirer comme modèle adulte par défaut' : 'Définir comme modèle piercing adulte par défaut'}
                      >
                        {template.isDefaultPiercing ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-100 text-primary-700 border border-primary-200">
                            ★ Défaut
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-400 border border-gray-200 hover:bg-primary-50 hover:text-primary-600 transition-colors">
                            ☆ Définir
                          </span>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <button
                        onClick={() => void handleSetDefaultPiercingMinor(template)}
                        className="inline-flex items-center gap-1.5 text-sm"
                        title={template.isDefaultPiercingMinor ? 'Retirer comme modèle mineur par défaut' : 'Définir comme modèle piercing mineur par défaut'}
                      >
                        {template.isDefaultPiercingMinor ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                            ★ Défaut
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-400 border border-gray-200 hover:bg-amber-50 hover:text-amber-600 transition-colors">
                            ☆ Définir
                          </span>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <button
                        onClick={() => void handleSetDefaultTattoo(template)}
                        className="inline-flex items-center gap-1.5 text-sm"
                        title={template.isDefaultTattoo ? 'Retirer comme modèle tatouage par défaut' : 'Définir comme modèle tatouage par défaut'}
                      >
                        {template.isDefaultTattoo ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700 border border-indigo-200">
                            ★ Défaut
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-400 border border-gray-200 hover:bg-indigo-50 hover:text-indigo-600 transition-colors">
                            ☆ Définir
                          </span>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{template.filePath}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatBytes(template.fileSize)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatDate(template.fileMtime)}</td>
                    <td className="px-4 py-3 text-sm space-x-2">
                      <button
                        onClick={() => void handlePreview(template)}
                        disabled={previewLoadingId === template.id}
                        className="inline-flex items-center px-3 py-1 rounded-md border border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-50"
                        title="Prévisualiser le PDF"
                      >
                        {previewLoadingId === template.id ? (
                          <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Eye className="h-4 w-4 mr-1" />
                        )}
                        Aperçu
                      </button>
                      <button
                        onClick={() => void handleDownload(template)}
                        className="inline-flex items-center px-3 py-1 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
                      >
                        <Download className="h-4 w-4 mr-1" />
                      {t('actions.download')}
                      </button>
                      <button
                        onClick={() => handleReplaceFile(template)}
                        disabled={replacingId === template.id}
                        className="inline-flex items-center px-3 py-1 rounded-md border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                        title="Remplacer le fichier Word"
                      >
                        <FolderOpen className="h-4 w-4 mr-1" />
                        {replacingId === template.id ? '…' : 'Remplacer'}
                      </button>
                      <button
                        onClick={() => handleOpenEdit(template)}
                        className="inline-flex items-center px-3 py-1 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                      {t('actions.edit')}
                      </button>
                      <button
                        onClick={() => void handleDelete(template)}
                        className="inline-flex items-center px-3 py-1 rounded-md border border-red-200 text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                      {t('actions.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </ResponsiveTable>
        </div>
      </div>

      <input
        ref={replaceFileInputRef}
        type="file"
        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        onChange={handleReplaceFileChange}
      />

      {previewUrl && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black bg-opacity-70">
          <div className="flex items-center justify-between bg-white px-6 py-3 shadow">
            <span className="text-sm font-medium text-gray-800">Prévisualisation du modèle</span>
            <button
              onClick={handleClosePreview}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm"
            >
              <X className="h-4 w-4" />
              Fermer
            </button>
          </div>
          <iframe
            src={previewUrl}
            className="flex-1 w-full border-0"
            title="Aperçu PDF"
          />
        </div>
      )}

      {editingTemplate && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('documentTemplates.modal.title')}</h3>
                <p className="text-sm text-gray-600">
                  {t('documentTemplates.modal.meta', {
                    code: editingTemplate.code,
                    language: editingTemplate.language,
                    version: editingTemplate.version,
                  })}
                </p>
              </div>
              <button
                onClick={() => setEditingTemplate(null)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                {t('actions.close')}
              </button>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                {t('documentTemplates.form.description')}
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  rows={3}
                  placeholder={t('documentTemplates.form.descriptionPlaceholder')}
                />
              </label>

              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-700">
                  {t('documentTemplates.form.active')}
                </span>
                <button
                  type="button"
                  onClick={() => setEditIsActive((prev) => !prev)}
                  className="inline-flex items-center text-sm text-gray-700"
                >
                  {editIsActive ? (
                    <ToggleRight className="h-5 w-5 text-green-600" />
                  ) : (
                    <ToggleLeft className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Signatures requises</p>
                <div className="flex flex-wrap gap-3">
                  {ALL_SIGNER_ROLES.map((role) => (
                    <label key={role.value} className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={editRequiredSigners.includes(role.value)}
                        onChange={() => toggleEditSigner(role.value)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">{role.label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Le document sera considéré comme entièrement signé uniquement lorsque tous les signataires requis auront apposé leur signature.
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Emplacement des signatures</p>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="radio"
                      name="signaturePlacement"
                      value="LAST_PAGE"
                      checked={editSignaturePlacement === 'LAST_PAGE'}
                      onChange={() => setEditSignaturePlacement('LAST_PAGE')}
                      className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Dernière page</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="radio"
                      name="signaturePlacement"
                      value="INLINE"
                      checked={editSignaturePlacement === 'INLINE'}
                      onChange={() => setEditSignaturePlacement('INLINE')}
                      className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Intégrée au document</span>
                  </label>
                </div>
                {editSignaturePlacement === 'INLINE' && (
                  <p className="text-xs text-amber-600 mt-1 bg-amber-50 border border-amber-200 rounded p-2">
                    Mode intégré : placez <code className="font-mono">{'[[SIGN_CLIENT]]'}</code>,{' '}
                    <code className="font-mono">{'[[SIGN_PRACTITIONER]]'}</code>,{' '}
                    <code className="font-mono">{'[[SIGN_LEGAL_GUARDIAN]]'}</code> ou{' '}
                    <code className="font-mono">{'[[SIGN_SHOP_MANAGER]]'}</code> dans le modèle DOCX.
                    La signature remplacera le marqueur dans le PDF généré.
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setEditingTemplate(null)}
                className="px-4 py-2 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
              >
                {t('actions.cancel')}
              </button>
              <button
                onClick={() => void handleSaveEdit()}
                disabled={editSubmitting}
                className="px-4 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {editSubmitting ? t('documentTemplates.actions.saving') : t('actions.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

